import {empty as observableEmpty, of as observableOf, Observable, of} from 'rxjs';

import {catchError, delay, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {
    AuditActivityId, AuditAutomationRunId,
    AuditFindingId,
    AuditId,
    AuditTemplateId,
    ProjectId, SpecId,
    TestObjectId,
} from '../../types/ids';
import {AuthService} from '../../services/auth.service';
import {
    ApiSourcedPaginationRequest,
    ApiSourcedPaginationResult,
    GenericApiResponse,
    APIResult,
    QueryMetadata
} from '../apibase.api';
import { HttpClient } from '@angular/common/http';
import {Audit, AuditActivitiesFilterSpec, AuditActivity, AuditAutomationRun, AuditReport, AuditTemplate, StandardAuditAutomationConfig, WorkflowSpec} from '@webmate-web-core/shared/services/audit.service';
import {AdHocReportSpec} from "@webmate-web-core/shared/types/testmgmt";
import {AuditActivityUpdate, AuditFinding, AuditReportConfig, AuditTemplateUpdateAction, AuditUpdateAction} from "@webmate-web-core/shared/types/audit";
import {ReportId} from "../../../../report/report_types";
import {AuditRequirement} from "@webmate-web-core/shared/tools/audit/audittemplate/shared";

@Injectable({
    providedIn: 'root'
})
export class AuditApi {

    constructor(public OAuth2AuthService: AuthService, private http: HttpClient) {}

    getAuditTemplate(id: AuditTemplateId): Observable<AuditTemplate> {
        return this.http.get<AuditTemplate>(`/audit/audit-templates/${id}`);
    }

    getAuditTemplatesInProject(projectId: ProjectId): ApiSourcedPaginationRequest<AuditTemplate> {
        return new ApiSourcedPaginationRequest<AuditTemplate>(`/projects/${projectId}/audit/audit-templates`, this.http, undefined, (res) => res as AuditTemplate);
    }


    deleteAuditTemplate(auditTemplateId: AuditTemplateId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/audit/audit-templates/${auditTemplateId}`);
    }

    updateAuditTemplate(id: AuditTemplateId, actions: AuditTemplateUpdateAction[]): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>(`/audit/audit-templates/${id}`, actions);
    }

    duplicateAuditTemplate(from: AuditTemplateId, targetProject: ProjectId): Observable<AuditTemplateId> {
        return this.http.post<AuditTemplateId>(`/projects/${targetProject}/audit/audit-templates?from=${from}`, {});
    }

    createAuditTemplate(projectId: ProjectId, name: string, description: string, workflowSpec: WorkflowSpec): Observable<AuditTemplateId> {
        let body: any = { name, description, workflowSpec };
        return this.http.post<AuditTemplateId>(`/projects/${projectId}/audit/audit-templates`, body);
    }


    /// Audits

    startAudit(projectId: ProjectId, title: string, scope: string, templateId: AuditTemplateId, specFilterName?: string): Observable<AuditId> {
        const postBody = {
            templateId,
            title,
            scope,
            specFilterName
        };

        return this.http.post<AuditId>(`/projects/${projectId}/audit/audits`, postBody);
    }

    startAutomationRun(auditId: AuditId, config?: StandardAuditAutomationConfig): Observable<AuditAutomationRunId> {
        return this.http.post<AuditAutomationRunId>(`/audit/audits/${auditId}/automations/workflow-check`, config);
    }

    getAuditAutomationRuns(auditId: AuditId): Observable<AuditAutomationRun[]> {
        return this.http.get<AuditAutomationRun[]>(`/audit/audits/${auditId}/automation-runs`);
    }

    getAuditAutomationRun(auditId: AuditId, automationRunId: AuditAutomationRunId): Observable<AuditAutomationRun> {
        return this.http.get<AuditAutomationRun>(`/audit/audits/${auditId}/automation-runs/${automationRunId}`);
    }

    getAudit(auditId: AuditId): Observable<Audit> {
        return this.http.get<Audit>(`/audit/audits/${auditId}`);
    }

    updateAudit(auditId: AuditId, updates: AuditUpdateAction[]) {
        return this.http.post<GenericApiResponse>(`/audit/audits/${auditId}`, updates);
    }

    deleteAudit(auditId: AuditId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>(`/audit/audits/${auditId}`);
    }

    queryAdhocAuditReports$(projectId: ProjectId, adhocReportSpec: AdHocReportSpec): Observable<any> {
        return this.http.post<any>(`/projects/${projectId}/audit/report`, adhocReportSpec).pipe(map((res) => res.data));
    }

    updateAuditFinding(auditActivityId: AuditActivityId, auditFinding: AuditFinding): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>(`/audit/audit-activities/${auditActivityId}/findings/${auditFinding.id}`, auditFinding);
    }

    updateAuditActivity$(id: AuditActivityId, updates: AuditActivityUpdate[]): Observable<AuditActivity> {
        return this.http.post<AuditActivity>(`/audit/audit-activities/${id}`, updates);
    }

    batchUpdateAuditActivities$(auditId: AuditId, ids: AuditActivityId[], updates: AuditActivityUpdate[]): Observable<GenericApiResponse> {
        const body = {
            auditId: auditId,
            ids: ids,
            updates: updates,
        };

        return this.http.post<GenericApiResponse>(`/audit/audit-activities/batch`, body);
    }

    getAuditActivity(auditActivityId: AuditActivityId): Observable<AuditActivity> {
        return this.http.get<AuditActivity>(`/audit/audit-activities/${auditActivityId}`);
    }

    getAuditActivitiesOfAudit(auditId: AuditId): Observable<AuditActivity[]> {
        return this.http.get<AuditActivity[]>(`/audit/audit-activities?auditId=${auditId}`);
    }

    queryAuditActivitiesOfAudit$(auditId: AuditId, filter: AuditActivitiesFilterSpec): Observable<AuditActivity[]> {
        return this.http.post<AuditActivity[]>(`/audit/audits/${auditId}/activities`, filter);
    }

    updateTestObjectsOfAudit(auditId: AuditId, testObjects: TestObjectId[]): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>(`/audit/audits/${auditId}/workflow/test-objects`, testObjects);
    }

    updateTestObjectsOfAuditTemplate(auditTemplateId: AuditTemplateId, testObjects: TestObjectId[]): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>(`/audit/audit-templates/${auditTemplateId}/workflow/test-objects`, testObjects);
    }

    updateTestObjectPlanningMatrix(auditId: AuditId, planningMatrix: { [testObjectId: string]: string[] }): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>(`/audit/audits/${auditId}/workflow/planning-matrix`, planningMatrix);
    }

    updateRequirementsOfAudit(auditId: AuditId, requirements: AuditRequirement[]): Observable<GenericApiResponse> {
        return this.http.put<GenericApiResponse>(`/audit/audits/${auditId}/workflow/requirements`, requirements);
    }
    generateAuditReport(auditId: AuditId, reportConfig: AuditReportConfig): Observable<ReportId> {
        return this.http.post<ReportId>(`/audit/audits/${auditId}/report-preview`, reportConfig);
    }

}
